/**
 * Utility functions for date formatting
 */

/**
 * Converts a date string to relative time format (e.g., "2 days ago", "1 week ago")
 * @param dateString - Date string in format "Monday, 10 Jan 2026" or ISO format
 * @returns Relative time string (e.g., "2 days ago", "1 week ago", "1 month ago")
 */
export function getTimeAgo(dateString: string): string {
  if (!dateString) return "";

  try {
    // Parse the date string
    // Handle format like "Monday, 10 Jan 2026" or ISO format
    let date: Date;
    
    // First try ISO format (most reliable)
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      date = isoDate;
    } else if (dateString.includes(",")) {
      // Format: "Monday, 10 Jan 2026" or "SATURDAY, 10 JAN 2026"
      const datePart = dateString.split(",")[1].trim();
      const dateParts = datePart.split(" ").filter(part => part.length > 0);
      
      if (dateParts.length >= 3) {
        const day = parseInt(dateParts[0]);
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthNamesUpper = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
        const monthStr = dateParts[1];
        const month = monthNames.indexOf(monthStr) !== -1 
          ? monthNames.indexOf(monthStr)
          : monthNamesUpper.indexOf(monthStr.toUpperCase());
        const year = parseInt(dateParts[2]);
        
        if (!isNaN(day) && month !== -1 && !isNaN(year)) {
          date = new Date(year, month, day);
        } else {
          return dateString; // Return original if parsing fails
        }
      } else {
        return dateString; // Return original if format is unexpected
      }
    } else {
      // Try other standard formats
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing fails
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle future dates
    if (diffInSeconds < 0) {
      return "today";
    }

    // Calculate days directly
    const diffInDays = Math.floor(diffInSeconds / (60 * 60 * 24));
    
    // If less than 24 hours, show "today"
    if (diffInDays < 1) {
      return "today";
    }

    // Days
    if (diffInDays < 7) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }

    // Weeks
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return diffInWeeks === 1 ? "1 week ago" : `${diffInWeeks} weeks ago`;
    }

    // Months
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
    }

    // Years
    const diffInYears = Math.floor(diffInDays / 365);
    return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original if error occurs
  }
}

